'use client';
import { useComputed } from '@legendapp/state/react';
import { useLayoutEffect, useState } from 'react';
import { motion, useMotionValue } from 'framer-motion';
import { AnimatePresenceShow } from '@/components/AnimatePresenceShow';
import { useHeroBlockSearchFormState } from '@/components/forms/HeroBlockSearchForm/HeroBlockSearchFormProvider';
import { HeroSearchFormMobileSearchBtn } from '@/components/forms/HeroBlockSearchForm/components/HeroSearchFormMobileSearchBtn';
import { cn } from '@/lib/cn';
import { motionPopLayout } from '@/styles/motionAnimations';

const spacing = 8;
const debug = false;

export type HeroSearchFormStickySearchBtnProps = {
    className?: string;
    id?: string;
};

export function HeroSearchFormStickySearchBtn({ id, className }: HeroSearchFormStickySearchBtnProps) {
    const { showStickySearchBtn$, variant$ } = useHeroBlockSearchFormState();
    const isVisible$ = useComputed(() => showStickySearchBtn$.get() && variant$.get() === 'vertical');
    const position = useMotionValue('fixed');
    const bottom = useMotionValue(0);
    const [container, setContainer] = useState<HTMLDivElement | null>(null);

    useLayoutEffect(() => {
        const btn = container;
        const footer = document.getElementById('site-footer');
        if (!footer || !btn) {
            return;
        }

        function setPosition(footerVisible?: boolean) {
            if (!footer || !btn) {
                return;
            }
            if (footerVisible) {
                const footerTop = footer.offsetTop;
                const btnTop = getElNatualPosition(btn);
                position.set('absolute');
                bottom.set(-(footerTop - btnTop - spacing));
            } else {
                position.set('fixed');
                bottom.set(spacing);
            }
        }

        const intersectionObserver = new IntersectionObserver(
            (entries) => {
                const footerVisible = entries.some((e) => e.isIntersecting);
                setPosition(footerVisible);
            },
            { root: null, threshold: 0 }
        );

        const resizeObserver = new ResizeObserver(() => {
            const footerVisible = footer.getBoundingClientRect().top < window.innerHeight;
            setPosition(footerVisible);
        });

        intersectionObserver.observe(footer);
        resizeObserver.observe(footer);

        return () => {
            intersectionObserver.disconnect();
            resizeObserver.disconnect();
        };
    }, [bottom, position, container]);

    return (
        <AnimatePresenceShow if={debug || isVisible$} mode="wait">
            <motion.div
                ref={(el) => setContainer(el)}
                key="site-sticky-search-btn-container"
                className={cn('w-full', className)}
                style={{
                    position,
                    bottom,
                }}
                {...motionPopLayout}
            >
                <HeroSearchFormMobileSearchBtn className="w-full" id={id}>
                    Search for vehicles
                </HeroSearchFormMobileSearchBtn>
            </motion.div>
        </AnimatePresenceShow>
    );
}

function getElNatualPosition(el: HTMLElement) {
    const btnPosition = el.style.position;
    const btnBottom = el.style.bottom;
    el.style.position = 'absolute';
    el.style.bottom = '0';
    const btnTop = el.offsetTop + el.offsetHeight;
    el.style.position = btnPosition;
    el.style.bottom = btnBottom;
    return btnTop;
}
